//#region IMPORT
import { useState, useRef } from 'react';
/** Services Import */
import { useDispatch, useSelector } from 'react-redux';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import { closeLoading, showLoading } from '../../../../Reducer/LoadingReducer';
import { v4 as uuidv4 } from 'uuid';
import ExcelJS from 'exceljs';
/** Components */
import { DEP_HAN } from './index';
import useCompanyCustDep from '../../../../hooks/useCompanyCustDep';
import { setRowData } from '../../../../Reducer/GridReducer';
import { createInitial } from '../../../../util';

import { DEP_HAN_Schema } from './DEP_HAN_Schema';
import { useDelete1, useSearch1, useSave1 } from './DEP_HAN_QUERY';
//#endregion

//#region 상수
const COLUMN_KEYS = ['depNm1', 'depNm2', 'depNm3', 'depNm4'];
const HEADER_MAP = {
  사업장명: 'depNm1',
  회사명: 'depNm2',
  부서명: 'depNm3',
  파트: 'depNm4',
};
//#endregion

function DEP_HANF() {
  //#region 변수
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const searchInfo = useSelector((s) => s.SearchInfo);

  const gridState1 = useSelector((state) => state.grid.grid1);
  const setRowData1 = (rows) =>
    dispatch(setRowData({ gridId: 'grid1', rowData: rows }));

  const [inputs, setInputs] = useState(() => createInitial(DEP_HAN_Schema));
  const inputRef = useRef(null); // 사업장명 입력칸 포커스용
  const fileInputRef = useRef(null);

  const { handleCoChange, handleCustChange, validateSelection } =
    useCompanyCustDep({ requireCompany: true, requireCust: true });

  const deleteDepartmentMutation = useDelete1();
  const { runAsync: Save1Async } = useSave1();
  const { refetch: Search1 } = useSearch1();
  //#endregion

  //#region 저장
  const ResetLogic = () => {
    setInputs(createInitial(DEP_HAN_Schema));
  };

  // 하위 단계까지 입력했을 때, 아직 등록되지 않은 상위 조직 행을 자동으로 함께 생성
  const makeParentDeps = (changedRows, existingRows) => {
    const existSet = new Set(
      existingRows.map((r) => `${r.depNm1}|${r.depNm2}|${r.depNm3}|${r.depNm4}`)
    );

    const makeRow = (nm1, nm2, nm3, nm4, isUserInput = false) => ({
      no: uuidv4(),
      depNm1: nm1 || '',
      depNm2: nm2 || '',
      depNm3: nm3 || '',
      depNm4: nm4 || '',
      depCd: isUserInput ? uuidv4() : '',
      newYn: 'Y',
    });

    let newRows = [];

    for (const row of changedRows) {
      const { depNm1, depNm2, depNm3, depNm4 } = row;
      if (depNm1 && !existSet.has(`${depNm1}|||`)) {
        newRows.push(makeRow(depNm1));
        existSet.add(`${depNm1}|||`);
      }
      if (depNm1 && depNm2 && !existSet.has(`${depNm1}|${depNm2}||`)) {
        newRows.push(makeRow(depNm1, depNm2));
        existSet.add(`${depNm1}|${depNm2}||`);
      }
      if (
        depNm1 &&
        depNm2 &&
        depNm3 &&
        !existSet.has(`${depNm1}|${depNm2}|${depNm3}|`)
      ) {
        newRows.push(makeRow(depNm1, depNm2, depNm3));
        existSet.add(`${depNm1}|${depNm2}|${depNm3}|`);
      }
      if (
        depNm1 &&
        depNm2 &&
        depNm3 &&
        depNm4 &&
        !existSet.has(`${depNm1}|${depNm2}|${depNm3}|${depNm4}`)
      ) {
        newRows.push(makeRow(depNm1, depNm2, depNm3, depNm4, true));
        existSet.add(`${depNm1}|${depNm2}|${depNm3}|${depNm4}`);
      }
    }

    return newRows;
  };

  // 저장/삭제 API 응답에서 실제 성공/실패 건수를 판별
  // (응답 형태가 배열이거나 {data:[...]}이거나 제각각이라 정규화해서 확인)
  const CountApiResults = (res) => {
    let rows;
    if (Array.isArray(res)) rows = res;
    else if (res?.data) rows = Array.isArray(res.data) ? res.data : [res.data];
    else if (res) rows = [res];
    else rows = [];

    const isFailure = (r) =>
      r?.type === 'Error' || r?.isSuccess === false || r?.IsSuccess === false;
    const isSuccess = (r) => r?.isSuccess === true || r?.IsSuccess === true;

    const failRows = rows.filter(isFailure);
    const successRows = rows.filter(isSuccess);
    // isSuccess 플래그가 전혀 없는 레거시 응답은 명시적 실패가 없으면 성공으로 간주
    const ambiguousCount = rows.length - failRows.length - successRows.length;

    return {
      successCount: successRows.length + (failRows.length === 0 ? ambiguousCount : 0),
      failCount: failRows.length,
      firstErrorMsg: failRows[0]?.message ?? failRows[0]?.Message,
    };
  };

  // 폼에서 "등록/수정" 클릭 시: 선택된(depCd 있는) 경로면 그 행만 수정,
  // 새 경로 입력이면 상위 조직을 자동으로 함께 등록
  const SaveLogic1 = async () => {
    if (!validateSelection()) return;

    if (!inputs.depNm1 || !inputs.depNm1.trim()) {
      dispatch(
        showSnackbar({ message: '사업장명은 필수입니다.', type: 'warning' })
      );
      inputRef.current?.focus();
      return;
    }

    dispatch(showLoading());
    try {
      let rowsToSave;

      if (inputs.depCd) {
        // 기존 경로 수정
        rowsToSave = [{ ...inputs, custCd: searchInfo.custCd, editYn: 'Y' }];
      } else {
        // 신규 경로 등록 - 상위 조직 자동 생성
        const ensuredRows = makeParentDeps(
          [{ ...inputs }],
          gridState1.rowData
        );

        if (ensuredRows.length === 0) {
          dispatch(
            showSnackbar({
              message: '이미 등록된 데이터입니다.',
              type: 'warning',
            })
          );
          return;
        }

        rowsToSave = ensuredRows.map((row) => ({
          ...row,
          custCd: searchInfo.custCd,
        }));
      }

      const res = await Save1Async(rowsToSave);
      const { successCount, failCount, firstErrorMsg } = CountApiResults(res);

      if (successCount === 0) {
        dispatch(
          showSnackbar({
            message: firstErrorMsg || '저장에 실패했습니다.',
            type: 'error',
          })
        );
        return;
      }

      dispatch(
        showSnackbar({
          message:
            failCount > 0
              ? `${successCount}건 저장, ${failCount}건 실패했습니다. ${firstErrorMsg || ''}`
              : '저장되었습니다.',
          type: failCount > 0 ? 'warning' : 'success',
        })
      );
      ResetLogic();
      await Search1();
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '저장 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };
  //#endregion

  //#region 나머지 함수
  const HandleAddClick = () => {
    ResetLogic();
    inputRef.current?.focus();
  };

  // 트리 노드 클릭 시: 왼쪽 폼에 값 채움(수정 모드로 전환)
  const handleRowSelection = (rowData) => {
    setInputs({ ...rowData });
  };

  // 트리 노드의 삭제 아이콘 클릭
  const HandleDeleteNode = async (row) => {
    if (!window.confirm(`"${row.depNm1 || ''}" 경로를 삭제하시겠습니까?`))
      return;

    dispatch(showLoading());
    try {
      const res = await deleteDepartmentMutation.runAsync([row]);
      const { successCount, firstErrorMsg } = CountApiResults(res);

      if (successCount === 0) {
        dispatch(
          showSnackbar({
            message: firstErrorMsg || '삭제에 실패했습니다.',
            type: 'error',
          })
        );
        return;
      }

      dispatch(showSnackbar({ message: '삭제되었습니다.', type: 'success' }));
      if (inputs.depCd && inputs.depCd === row.depCd) ResetLogic();
      await Search1();
    } catch (e) {
      dispatch(
        showSnackbar({
          message: e?.message || '삭제 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };
  //#endregion

  //#region 엑셀 업/다운
  // 엑셀에서 읽은 한글 텍스트가 DB 저장값과 다른 유니코드 정규화 형태(NFD)로
  // 들어오면 화면엔 똑같아 보여도 문자열 비교/키 매칭이 실패함 - NFC로 통일
  const NormalizeText = (v) =>
    v !== null && v !== undefined ? String(v).trim().normalize('NFC') : '';

  const makeKeyString = (item) =>
    COLUMN_KEYS.map((k) => NormalizeText(item[k])).join('|');

  const isKeyEmpty = (key) => {
    if (key == null || key === '') return true;
    return key.split('|').every((p) => p.trim() === '');
  };

  /** 엑셀 업로드 - 신규/변경 건은 즉시 저장 */
  const ExcelUpload1 = async (file) => {
    try {
      dispatch(showLoading());

      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const worksheet = workbook.worksheets[0];
      if (!worksheet) {
        dispatch(
          showSnackbar({
            message: '엑셀 시트를 불러오지 못했습니다.',
            type: 'warning',
          })
        );
        return;
      }

      const headerRow = worksheet.getRow(1);
      const colCount = 4;
      const fieldKeys = [];
      for (let i = 1; i <= colCount; i++) {
        const cellVal = headerRow.getCell(i).value;
        const text =
          cellVal != null
            ? typeof cellVal === 'object' && cellVal.richText
              ? (cellVal.richText || []).map((t) => t?.text || '').join('')
              : String(cellVal).trim()
            : '';
        fieldKeys.push(HEADER_MAP[text] || `col${i}`);
      }

      const uploadedData = [];
      let rowIndex = 2;
      const maxRows = 2000;

      while (rowIndex <= maxRows) {
        const row = worksheet.getRow(rowIndex);
        const rowObj = {};
        let hasAny = false;
        for (let i = 0; i < fieldKeys.length; i++) {
          const cell = row.getCell(i + 1);
          let value = cell.value;
          if (value !== null && value !== undefined) {
            hasAny = true;
            if (typeof value === 'object' && value !== null && value.richText) {
              value = (value.richText || [])
                .map((t) => t?.text || '')
                .join('')
                .trim();
            } else {
              value = String(value).trim();
            }
          } else {
            value = '';
          }
          rowObj[fieldKeys[i]] = NormalizeText(value);
        }
        if (!hasAny) break;

        const key = makeKeyString(rowObj);
        if (!isKeyEmpty(key)) {
          rowObj.no = rowObj.no || `new_${Date.now()}_${rowIndex}`;
          uploadedData.push(rowObj);
        }
        rowIndex++;
      }

      if (uploadedData.length === 0) {
        dispatch(
          showSnackbar({
            message: '읽을 수 있는 데이터가 없습니다.',
            type: 'warning',
          })
        );
        return;
      }

      const existingDataMap = new Map();
      (gridState1.rowData || []).forEach((row) => {
        const key = makeKeyString(row);
        if (key) existingDataMap.set(key, row);
      });

      const hasChanges = (existingRow, excelRow) => {
        for (const field of COLUMN_KEYS) {
          if (NormalizeText(existingRow[field]) !== NormalizeText(excelRow[field]))
            return true;
        }
        return false;
      };

      const rowsToSave = [];
      for (const excelRow of uploadedData) {
        const key = makeKeyString(excelRow);
        if (!key) continue;

        if (existingDataMap.has(key)) {
          const existingRow = existingDataMap.get(key);
          if (hasChanges(existingRow, excelRow)) {
            rowsToSave.push({
              ...existingRow,
              ...excelRow,
              editYn: 'Y',
              depCd: existingRow.depCd ?? '',
              custCd: searchInfo.custCd,
            });
          }
        } else {
          rowsToSave.push({
            ...excelRow,
            depCd: uuidv4(),
            newYn: 'Y',
            custCd: searchInfo.custCd,
          });
        }
      }

      if (rowsToSave.length === 0) {
        dispatch(
          showSnackbar({ message: '변경된 내용이 없습니다.', type: 'info' })
        );
        return;
      }

      const res = await Save1Async(rowsToSave);
      const { successCount, failCount, firstErrorMsg } = CountApiResults(res);

      if (successCount === 0) {
        dispatch(
          showSnackbar({
            message: firstErrorMsg || '저장에 실패했습니다.',
            type: 'error',
          })
        );
        return;
      }

      dispatch(
        showSnackbar({
          message:
            failCount > 0
              ? `${successCount}건 저장, ${failCount}건 실패했습니다. ${firstErrorMsg || ''}`
              : `${successCount}건이 저장되었습니다.`,
          type: failCount > 0 ? 'warning' : 'success',
        })
      );
      await Search1();
    } catch (error) {
      console.error('Excel upload error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 업로드 중 오류가 발생했습니다: ${error.message}`,
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };

  /** 엑셀 다운로드 */
  const ExcelDownload1 = async () => {
    try {
      if (!gridState1.rowData || gridState1.rowData.length === 0) {
        dispatch(
          showSnackbar({
            message:
              '다운로드할 데이터가 없습니다. 사업장을 선택했는지 확인해주세요.',
            type: 'warning',
          })
        );
        return;
      }

      dispatch(showLoading());

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('부서정보', {
        views: [{ state: 'frozen', ySplit: 1 }],
      });

      const headers = ['사업장명', '회사명', '부서명', '파트'];
      const headerRow = sheet.getRow(1);
      headers.forEach((h, i) => {
        headerRow.getCell(i + 1).value = h;
        headerRow.getCell(i + 1).font = { bold: true };
      });
      headerRow.commit();

      const detailData = gridState1.rowData || [];
      detailData.forEach((item, index) => {
        const row = sheet.getRow(2 + index);
        COLUMN_KEYS.forEach((key, i) => {
          const cell = row.getCell(i + 1);
          cell.value = item[key] ?? '';
          cell.font = { name: '맑은 고딕', size: 10 };
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'left',
            wrapText: false,
          };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF808080' } },
            left: { style: 'thin', color: { argb: 'FF808080' } },
            bottom: { style: 'thin', color: { argb: 'FF808080' } },
            right: { style: 'thin', color: { argb: 'FF808080' } },
          };
        });
        row.commit();
      });

      const now = new Date();
      const dateTimeStr =
        `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_` +
        `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}`;
      const fileName = `부서관리_${dateTimeStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(link.href);

      dispatch(
        showSnackbar({
          message: '엑셀 다운로드가 완료되었습니다.',
          type: 'success',
        })
      );
    } catch (error) {
      console.error('Excel download error:', error);
      dispatch(
        showSnackbar({
          message: `엑셀 다운로드 중 오류가 발생했습니다: ${error.message}`,
          type: 'error',
        })
      );
    } finally {
      dispatch(closeLoading());
    }
  };
  //#endregion

  return (
    <div className="wrap">
      <DEP_HAN
        userInfo={userInfo}
        inputs={inputs}
        setInputs={setInputs}
        inputRef={inputRef}
        fileInputRef={fileInputRef}
        rowData={gridState1.rowData}
        // 함수
        SearchLogic1={Search1}
        SaveLogic1={SaveLogic1}
        HandleAddClick={HandleAddClick}
        ResetLogic={ResetLogic}
        ExcelUpload1={ExcelUpload1}
        ExcelDownload1={ExcelDownload1}
        handleRowSelection={handleRowSelection}
        HandleDeleteNode={HandleDeleteNode}
        handleCoChange={handleCoChange}
        handleCustChange={handleCustChange}
        validateSelection={validateSelection}
      />
    </div>
  );
}

export default DEP_HANF;
